import { useEffect, useRef, useState } from 'react'

import type { Locale, Media } from '@/lib/types'

interface SmartImageProps {
  media: Media | undefined
  locale: Locale
  /** Overrides the alt text stored with the photo. */
  alt?: string
  className?: string
  /** The sizes attribute — tells the browser how wide the image will render. */
  sizes?: string
  /** Above-the-fold images should load eagerly and be given fetch priority. */
  priority?: boolean
  /** Which generated size to use when the browser cannot choose. */
  preferred?: 'thumb' | 'card' | 'full'
}

/**
 * A photograph with everything a gallery needs it to have.
 *
 * - `srcset` so a phone downloads the 400px file, not the 1600px one
 * - width and height so the layout does not jump as images arrive
 * - a blurred inline placeholder instead of a white rectangle
 * - lazy loading for everything below the fold
 *
 * Together these are most of what makes a photo-heavy page feel fast, and all
 * of what stops it from scoring badly on Core Web Vitals.
 */
export function SmartImage({
  media,
  locale,
  alt,
  className = '',
  sizes = '(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw',
  priority = false,
  preferred = 'card',
}: SmartImageProps) {
  const imageRef = useRef<HTMLImageElement>(null)
  const [loaded, setLoaded] = useState(false)

  /**
   * The photograph is revealed by the load event — but that event is easy to
   * miss, and missing it left the blurred placeholder in place over a picture
   * that had downloaded perfectly well.
   *
   * It happens whenever the image finishes before React is listening: the page
   * arrives as ready-made HTML, the browser starts fetching immediately, and on
   * a reload the file is in the cache and complete before hydration has run.
   * The event fired into the void, `loaded` stayed false, and the visitor was
   * left looking at the twenty-pixel preview.
   *
   * Asking the element directly covers that case: `complete` with a real
   * `naturalWidth` means the picture is there, whether or not we heard about
   * it. Re-running when the photo changes doubles as the reset — a new file
   * starts hidden again until it, too, is ready.
   */
  useEffect(() => {
    const image = imageRef.current
    setLoaded(Boolean(image?.complete && image.naturalWidth > 0))
  }, [media?.id])

  if (!media) {
    return (
      <div
        className={`flex items-center justify-center bg-stone-100 ${className}`}
        aria-hidden="true"
      />
    )
  }

  const altText = alt ?? ((locale === 'uk' ? media.altUk : media.altEn) || media.altUk)

  // Only the JPEG variants exist today. The format column is in the database
  // so WebP can be added later without touching this component's contract.
  const jpegVariants = media.variants.filter((v) => v.format === 'jpeg')
  const srcSet = jpegVariants.map((v) => `${v.url} ${v.width}w`).join(', ')

  const fallback =
    jpegVariants.find((v) => v.label === preferred) ??
    jpegVariants.at(-1) ??
    ({ url: media.url, width: media.width, height: media.height } as const)

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {media.placeholder && !loaded && (
        <div
          className="lqip absolute inset-0"
          style={{ backgroundImage: `url(${media.placeholder})` }}
          aria-hidden="true"
        />
      )}
      <img
        ref={imageRef}
        src={fallback.url}
        srcSet={srcSet || undefined}
        sizes={srcSet ? sizes : undefined}
        width={media.width}
        height={media.height}
        alt={altText}
        loading={priority ? 'eager' : 'lazy'}
        // fetchPriority is a hint that the hero image should jump the queue.
        fetchPriority={priority ? 'high' : 'auto'}
        decoding="async"
        onLoad={() => setLoaded(true)}
        // A photo that fails to load should show as a broken image with its
        // alt text, not as a blurred smudge that looks deliberate.
        onError={() => setLoaded(true)}
        className={`relative h-full w-full object-cover transition-opacity duration-500 ${
          loaded ? 'opacity-100' : 'opacity-0'
        }`}
      />
    </div>
  )
}
